import React from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/FiberManualRecord";
import Breadcrumbs from "../common/breadcrumbs";

const breadcrumbs = [
  { id: 0, name: "Home", href: "./" },
  { id: 1, name: "Change Log", href: "changelog" },
];
const changelogArray = [
  {
    version: "2.3.17",
    date: "25.07.2025",
    changes: [
      {
        title: "Added",
        subpoints: [
          "Added support to show devices/machines pages based on organization attribute instead of devices/machines endpoint.",
        ],
      },
      {
        title: "Changed",
        subpoints: [
          "Made REACT_APP_PUBLIC_URL in .env file more dynamic",
          "Changed default tank size of nngen2_level device to 5000",
          "Enhanced fleet select for machines/devices page to look like home page one.",
          "Increased max length for email and password fields in login boxes.",
        ],
      },
      {
        title: "Fixed",
        subpoints: [
          "Select field label fix in Organization Theme Service",
          "Fixed opening device history from the device page.",
          "Fixed assign users issue.",
        ],
      },
    ],
  },
  {
    version: "2.3.16",
    date: "15.07.2025",
    changes: [
      {
        title: "Added",
        subpoints: [
          'add/edit LdapServer: select status from "active, paused, cancelled" instead of textfield',
        ],
      },
      {
        title: "Changed",
        subpoints: [
          "use operation_id instead of uid",
          "updated New Device Details",
          "updated Login api",
        ],
      },
      {
        title: "Fixed",
        subpoints: [
          "profile page: fix bugs leading to not saving properly",
          "fixed loading organization without switch to it and no putting the key in url",
        ],
      },
    ],
  },
  {
    version: "2.3.15",
    date: "11.07.2025",
    changes: [
      {
        title: "Added",
        subpoints: [
          "Login page: autofocus on Email",
          "add bread crumbs to 1.namedLocationConfig, 2.LDAPServers/Groups",
        ],
      },
      {
        title: "Changed",
        subpoints: [
          "update Machine wireframes List",
          "organizations: close menu on row change",
          "newTicket page: select template: close menu after selecting",
        ],
      },
      {
        title: "Fixed",
        subpoints: [
          "for all button, make sure text fit in the button even in small screens",
          "dashboard dropdown showing incorrect name (FleetCWS/FleetCPS)",
          "Selecting Profile Picture",
          "allow configuring LDAP server",
          'TicketView showing "not found" before api responds',
          "missing icon button colors",
          "left side panel populating before permissions are fetched",
          "ViewLDAPGroup: data not loading",
          "get userType according to active organization not first organization",
        ],
      },
    ],
  },
  {
    version: "2.3.12",
    date: "01.07.2025",
    changes: [
      {
        title: "Added",
        subpoints: [
          "Add/Edit attributes button in row on hubserver table",
          "Ability to open row in new tab using Ctrl + click In all Needed Buttons",
          "Switched to new endpoint for createUser",
        ],
      },
      {
        title: "Changed",
        subpoints: [
          "Helpdesk system update as needed",
          "Removed expand UI from hubserver table",
          "Removed 'order_by' field from ticket template",
          "Drag and drop sorting in ticket template inputs",
          "Updated user permissions and organizations handling",
          "Assigned users to roles",
          "Updated ticket layout",
          "Click-to-open on names on TableTemplate ex: tags page",
        ],
      },
      {
        title: "Fixed",
        subpoints: [
          "Bug where 'name' input loses focus after typing first letter",
          "Login and permissions issues",
          "ESLint enhancements",
          "Dashboard load issues",
          "homepage fleet dropdown",
          "Nerospec group homepage make helpdesk clickable and hide other wise",
        ],
      },
    ],
  },
];
function Changelog() {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        p: "12px",
        borderRadius: "16px",
        minHeight: "90vh",
        height: "fit-content",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "block", sm: "center" },
          height: "64px",
        }}
      >
        <Box sx={{ display: { xs: "block", sm: "flex" } }}>
          <Box sx={{ width: "100%" }}>
            {
              //<HomePagesMenu pageName={'Maintenance'} />
            }
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Typography
              sx={{ fontSize: "32px", mb: 0, color: theme.palette.custom.text }}
              variant="h2"
              gutterBottom
            >
              {
                // {capitalize(t('Devices'))}
              }
              {t("Change Log")}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider
        sx={{ my: "10px", backgroundColor: theme.palette.custom.borderColor }}
      />
      {changelogArray.map((release, index) => (
        <>
          <Accordion
            key={index}
            defaultExpanded
            sx={{ backgroundColor: "inherit" }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Version {release.version} ({release.date})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {release.changes.map((item, index) => (
                <React.Fragment key={index}>
                  <ListItem sx={{ pl: 1, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 20 }}>
                      <CircleIcon sx={{ fontSize: 8 }} />
                    </ListItemIcon>
                    <ListItemText
                      sx={{ m: 0 }}
                      primary={
                        <Typography
                          sx={{
                            fontSize: "14.4px",
                            lineHeight: 1,
                            m: 0,
                            color: theme.palette.custom.text,
                          }}
                          variant="body2"
                        >
                          {item.title}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {item.subpoints.map((sub, subIndex) => (
                    <ListItem
                      key={`${index}-${subIndex}`}
                      sx={{ pl: 5, py: 0.25 }}
                    >
                      <ListItemIcon sx={{ minWidth: 20 }}>
                        <CircleIcon sx={{ fontSize: 6 }} color="disabled" />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ m: 0 }}
                        primary={
                          <Typography
                            sx={{
                              fontSize: "14.4px",
                              lineHeight: 1,
                              m: 0,
                              color: theme.palette.custom.text,
                            }}
                            variant="caption"
                          >
                            {sub}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </AccordionDetails>
          </Accordion>
          <Divider
            sx={{
              my: "10px",
              backgroundColor: theme.palette.custom.borderColor,
            }}
          />
        </>
      ))}
    </Box>
  );
}
export default Changelog;
